import { AvailableExtra, QuoteExtras, QuoteParcels } from '~/lib/types/basket-types';
import useBasketSection from '~/hooks/use-basket-section';
import { FC, useCallback, useEffect, useMemo } from 'react';
import Section from '~/components/core/section/section';
import { ParcelProtection } from '~/components/parcel-details/protection/protection';
import { BasketSections } from '~/lib/types/enums';
import { useAnalytics } from '@parcel2go/npm-p2g-app';
import { BaseModuleProps } from './module-factory';
import useBasket from '~/hooks/use-basket';
import { useProtection } from '~/hooks/use-protection';
import useQuote from '~/hooks/use-quote';
import { GetUpdatedQuoteProtectionExtras, ResetQuoteProtectionExtras } from '../lib/utils/extras-utils';
import { Protection } from '~/lib/types/extra-types';
import { quoteProtectionExtrasFilter } from '~/lib/quotes';

const ProtectionModule: FC<BaseModuleProps> = ({ errors, wrapper }) => {
  const { track } = useAnalytics();
  const { loading, quoting } = useBasket();
  const { quoteErrors, quote } = useQuote();
  const { protection, currency } = useProtection();
  const section = useBasketSection<QuoteExtras>({ name: BasketSections.EXTRAS });
  const parcelSection = useBasketSection<QuoteParcels>({ name: BasketSections.PARCELS });
  const quotePageBaseProtection = useMemo(
    () => section.data.initialExtras?.filter((e) => e.key == Protection.BASE) || [],
    [section.data]
  );

  // The total value of all the parcels in the basket.
  const parcelValue = useMemo(() => {
    return parcelSection.data?.parcels?.reduce((acc, parcel) => acc + (parcel.value || 0), 0) || 0;
  }, [parcelSection.data]);

  const onChangeProtection = useCallback(
    (extra: string, selected: boolean) => {
      if (selected && section.data.extras.some((e) => e.key === extra)) {
        return;
      }

      section.update({
        ...section.data,
        extras: GetUpdatedQuoteProtectionExtras(extra, selected, section.data.extras, quotePageBaseProtection),
        userInteractedWithProtection: true
      });

      track({ type: 'protection_selection_changed', payload: { extra, selected } });
    },
    [section, track, quotePageBaseProtection]
  );

  const resetProtection = useCallback(() => {
    section.update({
      ...section.data,
      extras: ResetQuoteProtectionExtras(section.data.extras, quotePageBaseProtection)
    });

    track({ type: 'protection_selection_reset', payload: {} });
  }, [section, track, quotePageBaseProtection]);

  const options: AvailableExtra[] = useMemo(() => {
    // Invalid Quote
    if (quoteErrors?.length > 0) {
      return [];
    }

    // Free/Included Cover
    if ((protection.includedProtection || 0) > 0 && parcelValue <= (protection.includedProtection || 0)) {
      return [];
    }

    // Chose Base on the Quote Page so this should be the lowest amount selectable
    // In this case it would be Base and Full, no None option.
    if (quotePageBaseProtection.length > 0) {
      return protection.options;
    }

    // Limit to paid options, prioritising the protection with the most cover.
    const filteredOptions = protection.options
      .filter(quoteProtectionExtrasFilter)
      .sort((a, b) => b.price.net - a.price.net)
      .slice(0, 1) // Set to 1 to only show 'Full', 2 to show 'Base' and 'Full'
      .reverse();

    // Return all option including a none option.
    return [
      {
        key: Protection.NONE,
        price: { net: 0, gross: 0, tax: 0 },
        name: 'None',
        tags: [],
        metadata: {
          protectedAmount: protection.includedProtection || 0
        }
      },
      ...filteredOptions
    ];
  }, [quoteErrors, protection.includedProtection, protection.options, parcelValue, quotePageBaseProtection]);

  const selectedOption = useMemo(
    () =>
      protection.options
        .filter(quoteProtectionExtrasFilter)
        .find((x) => section.data.extras.map((e) => e.key).includes(x.key)),
    [protection.options, section.data.extras]
  );

  const maxProtection = useMemo(
    () =>
      Math.max(
        options.map((o) => Math.max(o.metadata?.maxProtection || 0, o.metadata?.protectedAmount || 0)).reverse()[0] ||
          0,
        protection.includedProtection || 0,
        quote.service.maxProtection || 0
      ),
    [options, protection, quote.service]
  );

  const includedFreeOrBaseProtectionAmount = useMemo(() => {
    return (
      protection.includedProtection ||
      (quotePageBaseProtection.length > 0 &&
        selectedOption?.key == Protection.BASE &&
        selectedOption?.metadata?.protectedAmount) ||
      0
    );
  }, [protection, selectedOption, quotePageBaseProtection]);

  // If the user selected protection on the quote results, maintain the highest protection option for them, unless they manually select a different option.
  useEffect(() => {
    if (
      !section.data.userInteractedWithProtection &&
      includedFreeOrBaseProtectionAmount > 0 &&
      options.some((x) => x.key === Protection.FULL) &&
      (!selectedOption || selectedOption.key !== Protection.FULL)
    ) {
      section.update({
        ...section.data,
        extras: GetUpdatedQuoteProtectionExtras(Protection.FULL, true, section.data.extras, quotePageBaseProtection)
      });
    }
  }, [includedFreeOrBaseProtectionAmount, quotePageBaseProtection, options, selectedOption, section]);

  // Remove protection option if not longer avaliable after quote update.
  useEffect(() => {
    if (
      protection.requestId &&
      !quoteErrors?.length &&
      section.data.extras.some((x) => [Protection.BASE, Protection.FULL].includes(x.key)) &&
      !options.some((x) => section.data.extras.map((e) => e.key).includes(x.key))
    ) {
      resetProtection();
    }
  }, [options, section.data.extras, protection.requestId, resetProtection, quoteErrors]);

  const isFullyProtectedByIncludedOrBaseProtection =
    parcelValue > 0 && includedFreeOrBaseProtectionAmount >= parcelValue;

  const userHasFullyProtected =
    (selectedOption?.metadata?.protectedAmount || 0) >= (options[options.length - 1]?.metadata?.protectedAmount || 0);

  return wrapper(
    <Section dataTestId="protection-section" loading={section.loading || !protection || parcelSection.loading}>
      <ParcelProtection
        dataTestId="protection-section"
        quoteHasErrors={quoteErrors?.length > 0}
        quoting={loading || quoting || protection.loading}
        options={options}
        errors={errors}
        onChange={onChangeProtection}
        currency={currency}
        selected={selectedOption}
        parcelValue={parcelValue}
        serviceIncludedProtection={protection.includedProtection}
        maxProtection={maxProtection}
        userHasFullyProtected={userHasFullyProtected}
        includedFreeOrBaseProtectionAmount={includedFreeOrBaseProtectionAmount}
        isFullyProtectedByIncludedOrBaseProtection={isFullyProtectedByIncludedOrBaseProtection}
      />
    </Section>
  );
};

export default ProtectionModule;
